import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormArray, FormGroup, FormControl, Validators } from '@angular/forms';

import { Subject } from 'rxjs';
import { startWith, takeUntil, filter } from 'rxjs/operators';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { ValidateFormFields } from '@library/common/functions';
import { SvgIconsModule } from '@library/common/materials';
import { EnumsService } from '@library/core/services';
import { Container, containerGroup, containerArray, calcGroup, ShippingTypes } from '@library/common/models';

import { ShipmentService } from '../shipment.service';

@UntilDestroy()
@Component({
   selector: 'shipment-containers',
   templateUrl: './containers.component.html',
   styleUrls: ['./containers.component.scss']
})
export class ContainersComponent implements OnInit, OnDestroy {

   containers: FormArray;

   containerFormGroup: FormGroup = new FormGroup({
      containerArray: new FormArray([]),
   });

   shippingTypes = ShippingTypes;

   _listener$: Subject<void> = new Subject();

   get containerArray() { return this.containerFormGroup.get( 'containerArray' ) as FormArray; }
   get calculator() { return this.shipment.shipmentForm.get( 'calculator' ) as FormArray; }
   get shippingType() { return this.shipment.shipmentForm.get( 'shippingType' ) as FormControl; }
   get itemsType() { return this.shipment.shipmentForm.get( 'itemsType' ); }
   get itemized() { return this.shipment.shipmentForm.get( 'itemized' ).value; }

   containerArrayGroup( value = {} ): FormGroup {
      return new FormGroup({
         quantity: new FormControl( value['quantity'] || 1, [ Validators.required, Validators.min( 1 )]),
         type: new FormControl( value['type'] || '', [ Validators.required ]),
         containers: containerArray( value['containers'] ),
      });
   }

   constructor(
      private svgIcons: SvgIconsModule,
      public shipment: ShipmentService,
      public enums: EnumsService,
   ) { }

   ngOnInit() {
      ['FCL', 'LCL'].map( icon => this.svgIcons.register( icon ));

      this.shipment.shipmentForm$.pipe(
         untilDestroyed( this ),
      ).subscribe( s => {
         this.containers = s.get( 'containers' ) as FormArray;
      });

      // Listen for itemsType change and update accordingly
      this.itemsType.valueChanges.pipe(
         untilDestroyed( this ),
      ).subscribe( type => {
         switch ( type ) {
            case 'FCL':
               if ( this.calculator ) { this.calculator.clear(); }

               // add a blank one if empty
               if ( this.itemized && !this.containers.length ) {
                  this.containers.push( containerGroup() );
               }

               if ( !this.itemized ) {
                  // write into container array
                  const types: string[] = [ ...new Set(( this.containers.value as Container[] ).map( v => v.type ))];

                  types.map( t => {
                     const containers = this.containers.value.filter( v => v.type === t );
                     const quantity = containers.length;
                     this.addContainerArrayGroup({ quantity, type: t, containers });
                  });

                  // add a blank one if empty
                  if ( !this.containerArray.length ) {
                     this.addContainerArrayGroup();
                  }
               }

               break;
            case 'LCL':
            default:
               if ( this.containers ) { this.containers.clear(); }
               this.containerArray.clear();
         }
      });

      // Listen for Itemized Change only on FCL types
      this.shipment.shipmentForm.get( 'itemized' ).valueChanges.pipe(
         filter( _ => this.itemsType.value === 'FCL' ),
         untilDestroyed( this ),
      ).subscribe( itemized => {
         if ( itemized ) {
            // stop the containerArrayGroup listeners
            this._listener$.next();

            if ( !this.containers.length ) {
               this.addContainer();
            }

            setTimeout( _ => this.containerArray.clear(), 1 );
         } else {
            this.itemsType.setValue( this.itemsType.value );

            this.containerArray.controls.map(( group: FormGroup ) => {
               // reset calc items to 1pc 1wgt (non-itemized)
               const containers = group.get( 'containers' ) as FormArray;
               containers.controls.map( c => {
                  const items = c.get( 'items' ) as FormArray;
                  items.clear();
                  items.push( calcGroup() );
               });
            });
         }
      });

      // Sanitize shippingType for LCL vs FCL
      this.shipment.shipmentForm.get( 'shippingType' ).valueChanges.pipe(
         startWith( this.shippingType.value ), // load starting values
         untilDestroyed( this ),
      ).subscribe( type => {
         this.itemsType.setValue( type === 'FCL' ? 'FCL' : 'LCL' );
      });
   }

   ngOnDestroy() {
      this._listener$.next();
      this._listener$.complete();
   }

   addContainer( value?: any ) {
      this.containers.push( containerGroup( value ));
   }

   removeContainer( index: number ): void {
      this.containers.removeAt( index );
   }

   addContainerArrayGroup( value = {} ): void {
      this.containerArray.push( this.containerArrayGroup( value ));
      this.setListeners();
   }

   removeContainerArrayGroup( index: number ): void {
      this.containerArray.removeAt( index );
   }

   setListeners() {
      this._listener$.next();

      this.containerArray.controls.map(( group: FormGroup ) => {
         group.get( 'quantity' ).valueChanges.pipe(
            takeUntil( this._listener$ ),
         ).subscribe( quantity => {
            quantity = !quantity || quantity < 1 ? 1 : quantity;

            const containers = group.get( 'containers' ) as FormArray;
            let current = containers.length;

            if ( quantity === current ) { return; }
            if ( quantity > current ) {
               // adding containers
               Array.from({ length: quantity - current }, _ => containers.push( containerGroup({
                  type: group.get( 'type' ).value
               })));
            }
            if ( quantity < current ) {
               // removing containers
               while ( current > quantity || current <= 0 ) {
                  containers.removeAt( --current );
               }
            }
         });

         group.get( 'type' ).valueChanges.pipe(
            takeUntil( this._listener$ ),
         ).subscribe( type => {
            const containers = group.get( 'containers' ) as FormArray;
            containers.controls.map(( c: FormGroup ) => c.get( 'type' ).setValue( type ));
         });
      });

      this.containerFormGroup.valueChanges.pipe(
         takeUntil( this._listener$ ),
         startWith( true ),
      ).subscribe( _ => {
         this.containers.clear();

         this.containerArray.controls.map(( group: FormGroup ) => {
            const containers = group.get( 'containers' ) as FormArray;
            containers.controls.map(( c: FormGroup ) => this.containers.push( c ));
         });
      });

      this.shipment.highlightInvalid$.pipe(
         takeUntil( this._listener$ ),
         filter( bool => bool ),
      ).subscribe( _ => ValidateFormFields( this.containerFormGroup ));
   }
}
